import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

const TermsOfService = ({ onClose }) => {
  const { t } = useTranslation();
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
          <h1 className="text-2xl font-bold">{t('legal.terms.title')}</h1>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6 text-gray-700">
          <p className="text-sm text-gray-500">
            <strong>Last Updated:</strong> January 31, 2025
          </p>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. Acceptance of Terms</h2>
            <p className="leading-relaxed">
              By accessing or using Magic Moments ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
              If you do not agree to these Terms, please do not use the Service. These Terms constitute a legally binding 
              agreement between you and Magic Moments.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Description of Service</h2>
            <p className="leading-relaxed mb-2">
              Magic Moments provides an AI-powered platform that allows users to:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Upload and store photos and videos</li>
              <li>Generate animated moments using artificial intelligence</li>
              <li>Create and manage albums</li>
              <li>Collaborate with others on shared albums</li>
              <li>Purchase credits for service usage</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. User Accounts and Registration</h2>
            <div className="space-y-2">
              <p><strong>3.1 Account Creation:</strong> You must create an account to use certain features. You agree to provide accurate, current, and complete information during registration.</p>
              <p><strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</p>
              <p><strong>3.3 Age Requirement:</strong> You must be at least 13 years old to use this Service. Users under 18 require parental consent.</p>
              <p><strong>3.4 Account Termination:</strong> We reserve the right to suspend or terminate accounts that violate these Terms.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. User Content and Ownership</h2>
            <div className="space-y-2">
              <p><strong>4.1 Your Content:</strong> You retain all ownership rights to content you upload ("User Content").</p>
              <p><strong>4.2 License Grant:</strong> By uploading content, you grant Magic Moments a non-exclusive, worldwide, royalty-free license to use, process, and display your content solely for providing the Service.</p>
              <p><strong>4.3 Content Restrictions:</strong> You agree not to upload content that:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Violates any laws or third-party rights</li>
                <li>Contains malware, viruses, or harmful code</li>
                <li>Is pornographic, hateful, or promotes violence</li>
                <li>Infringes on intellectual property rights</li>
                <li>Contains personal information of others without consent</li>
              </ul>
              <p><strong>4.4 Content Moderation:</strong> We reserve the right to review and remove content that violates these Terms without prior notice.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Credits and Payment</h2>
            <div className="space-y-2">
              <p><strong>5.1 Credit System:</strong> Certain features require credits, which can be purchased through the Service.</p>
              <p><strong>5.2 Payment Processing:</strong> All payments are processed securely through third-party payment processors.</p>
              <p><strong>5.3 No Refunds:</strong> Credit purchases are non-refundable except as required by law or in cases of service failure.</p>
              <p><strong>5.4 Pricing Changes:</strong> We reserve the right to modify pricing with 30 days notice to active users.</p>
              <p><strong>5.5 Credit Expiration:</strong> Credits do not expire but may be forfeited upon account termination for violations.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Refund and Return Policy</h2>
            <div className="space-y-2">
              <p>
                Our complete Refund and Return Policy governs all purchases made through Magic Moments, including physical products, 
                digital credits, and services. By making a purchase, you agree to the terms outlined in our policy.
              </p>
              <p><strong>6.1 Policy Overview:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Physical Products (Digital Frames):</strong> 14-day replacement eligibility for damaged or defective items (single replacement per order)</li>
                <li><strong>Digital Credits:</strong> Non-refundable once delivered to your account</li>
                <li><strong>Gifted Items:</strong> Non-refundable once redeemed by the recipient</li>
                <li><strong>Mail-In Service:</strong> Non-refundable once digitization is complete; reprocessing available for order specification issues</li>
              </ul>
              <p><strong>6.2 Full Policy:</strong> For complete details on returns, refunds, timeframes, and procedures, please review our{' '}
                <a href="/return-policy" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800 underline font-semibold">
                  Refund & Return Policy
                </a>.
              </p>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                <strong>Note:</strong> This policy complies with Virginia consumer protection laws and is displayed at checkout. 
                All requests must be made within 14 days of delivery.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. AI-Generated Content</h2>
            <div className="space-y-2">
              <p><strong>7.1 AI Processing:</strong> Our Service uses artificial intelligence to analyze images and generate animated content.</p>
              <p><strong>7.2 No Guarantees:</strong> We do not guarantee the quality, accuracy, or suitability of AI-generated results.</p>
              <p><strong>7.3 Output Ownership:</strong> You own the AI-generated content created from your uploaded materials.</p>
              <p><strong>7.4 Model Training:</strong> Your content will not be used to train AI models without explicit consent.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Prohibited Conduct</h2>
            <p className="mb-2">You agree not to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Attempt to bypass security measures or access restrictions</li>
              <li>Use automated systems (bots, scrapers) without permission</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Impersonate others or misrepresent your affiliation</li>
              <li>Sell, transfer, or sublicense your account</li>
              <li>Reverse engineer or decompile any aspect of the Service</li>
              <li>Use the Service for any illegal purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">9. Intellectual Property</h2>
            <div className="space-y-2">
              <p><strong>9.1 Service IP:</strong> All rights, title, and interest in the Service (excluding User Content) belong to Magic Moments.</p>
              <p><strong>9.2 Trademarks:</strong> "Magic Moments" and related logos are trademarks of Magic Moments.</p>
              <p><strong>9.3 Copyright:</strong> All software, text, graphics, and other materials are protected by copyright laws.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">10. Privacy, Data Protection, and Security</h2>
            <div className="space-y-2">
              <p><strong>9.1 Privacy Policy:</strong> Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand our data collection and use practices.</p>
              <p><strong>9.2 Data Security Infrastructure:</strong> We utilize enterprise-grade security measures to protect your content:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Cloudflare Security:</strong> All data is protected by Cloudflare's global network, including DDoS protection, Web Application Firewall (WAF), and bot mitigation</li>
                <li><strong>Media Storage:</strong> Images stored on Cloudflare Images and videos on Cloudflare Stream with automatic encryption and optimization</li>
                <li><strong>Encrypted Transmission:</strong> All data transfers use TLS 1.3 encryption</li>
                <li><strong>Signed URLs:</strong> Media access uses cryptographically signed, time-limited URLs</li>
                <li><strong>Database Security:</strong> MongoDB Atlas with AES-256 encryption at rest and encrypted backups</li>
              </ul>
              <p><strong>9.3 Data Isolation:</strong> Your content is isolated from other users and accessible only through authenticated, authorized requests.</p>
              <p><strong>9.4 Compliance:</strong> Our infrastructure is hosted in ISO 27001 and SOC 2 Type II certified data centers.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">11. Disclaimers and Limitations of Liability</h2>
            <div className="space-y-2">
              <p><strong>11.1 "AS IS" Service:</strong> THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.</p>
              <p><strong>11.2 No Guarantee of Availability:</strong> We do not guarantee uninterrupted or error-free service.</p>
              <p><strong>11.3 Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, MAGIC MOMENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.</p>
              <p><strong>11.4 Maximum Liability:</strong> Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">12. Indemnification</h2>
            <p className="leading-relaxed">
              You agree to indemnify and hold Magic Moments harmless from any claims, damages, losses, or expenses 
              (including legal fees) arising from: (a) your use of the Service, (b) your User Content, 
              (c) your violation of these Terms, or (d) your violation of any rights of another party.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">13. Data Storage, Backup, and Loss</h2>
            <div className="space-y-2">
              <p><strong>12.1 Storage Infrastructure:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Images:</strong> Stored on Cloudflare Images with automatic optimization, global CDN delivery, and variant generation</li>
                <li><strong>Videos:</strong> Stored on Cloudflare Stream with adaptive bitrate streaming, thumbnail generation, and worldwide distribution</li>
                <li><strong>User Data:</strong> Stored in MongoDB Atlas with automatic daily backups and geographic replication</li>
              </ul>
              <p><strong>12.2 Backup Policy:</strong> We maintain automated backups of all user data with a retention period of 90 days.</p>
              <p><strong>12.3 User Responsibility:</strong> While we implement reasonable backup procedures, you are solely responsible for maintaining your own backups of User Content. We strongly recommend keeping local copies of important files.</p>
              <p><strong>12.4 No Liability for Loss:</strong> We are not liable for any loss, corruption, or unauthorized access to User Content, except as required by applicable law.</p>
              <p><strong>12.5 Data Redundancy:</strong> Your content is stored with redundancy across multiple data centers, but no system is completely immune to data loss.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">14. Third-Party Services and Infrastructure</h2>
            <div className="space-y-2">
              <p><strong>13.1 Third-Party Integrations:</strong> The Service integrates with trusted third-party providers:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Cloudflare:</strong> Content delivery, security, and media storage/streaming infrastructure</li>
                <li><strong>MongoDB Atlas:</strong> Database hosting and management</li>
                <li><strong>Stripe:</strong> Secure payment processing (we do not store payment card details)</li>
                <li><strong>SendGrid:</strong> Email delivery services</li>
                <li><strong>AI Providers:</strong> Third-party AI services for image analysis and video generation</li>
              </ul>
              <p><strong>13.2 Third-Party Terms:</strong> Your use of integrated services is also subject to their respective terms and privacy policies.</p>
              <p><strong>13.3 Third-Party Links:</strong> The Service may contain links to third-party websites. We are not responsible for the content, privacy practices, or terms of external sites.</p>
              <p><strong>13.4 Service Availability:</strong> Service functionality may be affected by third-party service outages or disruptions beyond our control.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">15. Modifications to Service and Terms</h2>
            <div className="space-y-2">
              <p><strong>14.1 Service Changes:</strong> We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time.</p>
              <p><strong>14.2 Terms Updates:</strong> We may update these Terms periodically. Continued use after changes constitutes acceptance of new Terms.</p>
              <p><strong>14.3 Notification:</strong> Material changes will be communicated via email or Service notification.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">16. Termination</h2>
            <div className="space-y-2">
              <p><strong>15.1 By You:</strong> You may terminate your account at any time through account settings.</p>
              <p><strong>15.2 By Us:</strong> We may terminate or suspend your account for violations of these Terms.</p>
              <p><strong>15.3 Effect:</strong> Upon termination, your right to use the Service ceases immediately. We may delete your content after 30 days.</p>
              <p><strong>15.4 Survival:</strong> Sections regarding ownership, indemnification, disclaimers, and limitations survive termination.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">17. Dispute Resolution</h2>
            <div className="space-y-2">
              <p><strong>16.1 Informal Resolution:</strong> Before filing a claim, you agree to contact us to attempt informal resolution.</p>
              <p><strong>16.2 Arbitration:</strong> Any disputes shall be resolved through binding arbitration rather than in court, except for small claims.</p>
              <p><strong>16.3 Class Action Waiver:</strong> You agree to bring claims only in your individual capacity, not as a class action.</p>
              <p><strong>16.4 Governing Law:</strong> These Terms are governed by the laws of [Your Jurisdiction], without regard to conflict of law principles.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">18. General Provisions</h2>
            <div className="space-y-2">
              <p><strong>17.1 Entire Agreement:</strong> These Terms constitute the entire agreement between you and Magic Moments.</p>
              <p><strong>17.2 Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in effect.</p>
              <p><strong>17.3 No Waiver:</strong> Failure to enforce any right does not constitute a waiver of that right.</p>
              <p><strong>17.4 Assignment:</strong> You may not assign these Terms. We may assign our rights to any successor.</p>
              <p><strong>17.5 Force Majeure:</strong> We are not liable for delays or failures due to circumstances beyond our control.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">19. Contact Information</h2>
            <p className="leading-relaxed">
              For questions about these Terms, please contact us at:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-2">
              <p><strong>Email:</strong> support@magic-moments.ai</p>
            </div>
          </section>

          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-sm text-gray-500 italic">
              By using Magic Moments, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 p-4 rounded-b-2xl border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TermsOfService;
